/**
 * Mailbox domain: the mock behind `GET /domains/{domain}`, one per section.
 *
 * TEMPORARY, like the seed beside it. The DOMAIN carries the asynchronous part of mail setup
 * (DNS), not the mailbox: creation is synchronous, so a mailbox signs in and sends at once while
 * its domain may still be `dns_pending` for incoming mail. There are no webhooks, so the real UI
 * polls this endpoint while the domain is being set up (see `mailboxDomainPolls`); the section's poll loop
 * is real, and only the read
 * below is mocked. When the endpoint lands, swap {@link pollMockMailboxDomain} for the fetch.
 */
import {
  MAILBOX_VARIANT_COUNT,
  mailboxDomainReadyFallback,
  type DomainStatus,
  type MailboxDomain,
  type MailboxPrice,
} from './mailbox-records';

/**
 * The domain for a brand at cycle position `variant`, on `domain`:
 *  - 0 -> `dns_verified`: registered, DNS done, no mailboxes yet (the state the last delete walks
 *         an `active` domain back to).
 *  - 2 -> `dns_pending`: the mailboxes sign in and send now, and incoming mail is on its way, so
 *         the section's notice and its poll run from seed data.
 *  - 5 -> `failed`, with the provider's reason, so the error notice renders from seed data.
 *  - 6 -> `null`: the service has NEVER HEARD OF the domain (`GET /domains/{domain}` and its list
 *         both answer `404 not_found`). Most brand domains start here: the first create registers
 *         the domain, orders the service and publishes DNS in one call (see the reducer's `create`).
 *  - every other variant -> `active`.
 */
/**
 * The simulation's knob for tests: every domain the mock builds from now on answers `status`
 * instead of its variant's (`null` restores the variants), so a component test can render a domain
 * in a status no variant carries (`relay_pending`, `forward_pending`). Gone with the mock.
 */
let statusOverride: DomainStatus | null = null;
export const simulateMockDomainStatus = (status: DomainStatus | null): void => {
  statusOverride = status;
};

export const buildMailboxDomain = (variant: number, rawDomain: string): MailboxDomain | null => {
  const v = ((variant % MAILBOX_VARIANT_COUNT) + MAILBOX_VARIANT_COUNT) % MAILBOX_VARIANT_COUNT;
  if (v === 6) return null;
  const domain = rawDomain.trim().toLowerCase();
  const status: DomainStatus =
    statusOverride ?? (v === 0 ? 'dns_verified' : v === 2 ? 'dns_pending' : v === 5 ? 'failed' : 'active');
  return {
    domain,
    status,
    ready: mailboxDomainReadyFallback(status),
    manageDns: true,
    ...(status === 'failed'
      ? { failureReason: 'The mail order was rejected: the domain already has mail hosted with another provider.' }
      : {}),
  };
};

/** How many polls a `dns_pending` mock domain answers before it resolves. Mock only. */
export const MAILBOX_MOCK_DNS_POLLS = 3;

/** Polls answered per domain this tab: the mock's stand-in for time passing at the provider. */
const pollsAnswered = new Map<string, number>();

/**
 * One poll of the domain, as the endpoint would answer. A `dns_pending` domain resolves after
 * {@link MAILBOX_MOCK_DNS_POLLS} reads: to `active` when it holds a live mailbox, to
 * `dns_verified` when it holds none (the same rule the last delete walks back by). Any other
 * status answers unchanged. Resolves asynchronously, as the fetch it stands in for will.
 */
export const pollMockMailboxDomain = (current: MailboxDomain, liveMailboxes: number): Promise<MailboxDomain> => {
  if (current.status !== 'dns_pending') return Promise.resolve(current);
  const answered = (pollsAnswered.get(current.domain) ?? 0) + 1;
  pollsAnswered.set(current.domain, answered);
  if (answered < MAILBOX_MOCK_DNS_POLLS) return Promise.resolve(current);
  pollsAnswered.delete(current.domain);
  const status: DomainStatus = liveMailboxes > 0 ? 'active' : 'dns_verified';
  return Promise.resolve({ ...current, status, ready: mailboxDomainReadyFallback(status) });
};

// --- `GET /domains/{domain}/mailbox-price` -------------------------------------------------------

/**
 * The mock SERVICE's answer to the price read: the contract's own example. A stand-in for the
 * service, like every other answer in this folder; it is not where the product decides what a
 * mailbox costs (nothing in the app does), and nothing but {@link fetchMockMailboxPrice} reads it.
 */
const MOCK_SERVICE_PRICE: MailboxPrice = { amount: 10, currency: 'USD' };

/** How long the mock price read takes to answer. Mock only. */
export const MAILBOX_MOCK_PRICE_DELAY_MS = 250;

/**
 * The simulation's knob for tests: the price read answers this instead (`0` is charging switched
 * off), `'fail'` rejects it, `'never'` leaves it unanswered; `null` restores the default. Gone with
 * the mock.
 */
let priceOverride: MailboxPrice | 'fail' | 'never' | null = null;
export const simulateMockMailboxPrice = (answer: MailboxPrice | 'fail' | 'never' | null): void => {
  priceOverride = answer;
};

/** The price read, as the endpoint would answer it, after {@link MAILBOX_MOCK_PRICE_DELAY_MS}. */
export const fetchMockMailboxPrice = (): Promise<MailboxPrice> => {
  const answer = priceOverride ?? MOCK_SERVICE_PRICE;
  if (answer === 'never') return new Promise<MailboxPrice>(() => undefined);
  return new Promise<MailboxPrice>((resolve, reject) => {
    window.setTimeout(() => {
      if (answer === 'fail') reject(new Error('The price could not be read.'));
      else resolve(answer);
    }, MAILBOX_MOCK_PRICE_DELAY_MS);
  });
};
